/**
 * IntelHubContext — Cross-tab state for the AXEL Intel Hub.
 *
 * Provides:
 *   activeTab       "brief" | "research" | "chart" | ...
 *   setActiveTab
 *   fullAnalysisPayload   { asset, systemPrompt, userMessage } | null
 *   setFullAnalysisPayload
 *   lastBriefData   last generated Daily Brief JSON | null
 *   setLastBriefData
 *   activeSignal    last generated signal (Live Trigger / MT5 Setup) | null
 *                   shape: { symbol, direction, entry, stop_loss, tp1, tp2, tp3, source }
 *   setActiveSignal
 *   chartSymbol     app-label symbol the AssetChart should follow (one-way sync)
 *   setChartSymbol
 */
import React, { createContext, useContext, useState } from "react";

const IntelHubContext = createContext(null);

export function IntelHubProvider({ children }) {
  const [activeTab, setActiveTab] = useState("brief");
  const [fullAnalysisPayload, setFullAnalysisPayload] = useState(null);
  const [lastBriefData, setLastBriefData] = useState(null);
  const [activeSignal, setActiveSignal] = useState(null);
  const [chartSymbol, setChartSymbol] = useState("XAUUSD");

  return (
    <IntelHubContext.Provider
      value={{
        activeTab,
        setActiveTab,
        fullAnalysisPayload,
        setFullAnalysisPayload,
        lastBriefData,
        setLastBriefData,
        activeSignal,
        setActiveSignal,
        chartSymbol,
        setChartSymbol,
      }}
    >
      {children}
    </IntelHubContext.Provider>
  );
}

export function useIntelHub() {
  const ctx = useContext(IntelHubContext);
  if (!ctx) throw new Error("useIntelHub must be used within IntelHubProvider");
  return ctx;
}

// Non-throwing variant — safe for leaf components that may render outside
// the provider (e.g. LiveTriggerPanel reused elsewhere). Returns null then.
export function useIntelHubOptional() {
  return useContext(IntelHubContext);
}
